import { EditOutlined, MoreOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd";
import { Button, Dropdown, Input, Select, Table } from "antd";
import { EmptyState } from "@/shared/components/EmptyState";
import { TableToolbar } from "@/shared/components/TableToolbar";
import { useUrlTableState } from "@/shared/hooks/useUrlState";
import { formatTableIndex } from "@/shared/utils/formatTableIndex";
import { tablePagination } from "@/shared/utils/tablePagination";
import { usePlantList } from "../hooks/usePlants";
import { usePlantsStore } from "../store/plants.store";
import type { Plant, PlantType } from "../types";
import { PlantFormModal } from "./PlantFormModal";

const TYPE_OPTIONS: { value: PlantType; label: string }[] = [
	{ value: "DARAXT", label: "Daraxt" },
	{ value: "KO'CHAT", label: "Ko'chat" },
	{ value: "GUL", label: "Gul" },
];

const TYPE_LABEL: Record<PlantType, string> = {
	DARAXT: "Daraxt",
	"KO'CHAT": "Ko'chat",
	GUL: "Gul",
};

interface PlantsTableProps {
	organizationId: string;
}

export function PlantsTable({ organizationId }: PlantsTableProps) {
	const { page: currentPage, limit: pageSize, setPage, getFilter, setFilters } = useUrlTableState();
	const typeFilter = getFilter("type") as PlantType | undefined;
	const search = getFilter("search");
	const openEditModal = usePlantsStore((s) => s.openEditModal);

	const { data, isLoading } = usePlantList({
		organizationId,
		page: currentPage,
		limit: pageSize,
		type: typeFilter,
		search: search || undefined,
	});

	const items = data?.data?.items ?? [];
	const total = data?.data?.pagination?.total ?? 0;

	return (
		<>
			<TableToolbar
				filters={
					<>
						<Input.Search
							// Remount when the URL value changes so back/forward restores the text
							key={search ?? ""}
							placeholder="Qidirish"
							allowClear
							style={{ width: 200 }}
							defaultValue={search}
							onSearch={(v) => setFilters({ search: v || null })}
						/>
						<Select
							placeholder="Turi"
							allowClear
							style={{ width: 140 }}
							value={typeFilter}
							onChange={(v) => setFilters({ type: v ?? null })}
							options={TYPE_OPTIONS}
						/>
					</>
				}
			/>
			<Table
				loading={isLoading}
				locale={{
					emptyText: <EmptyState description="Ekinlar topilmadi" />,
				}}
				pagination={tablePagination({
					page: currentPage,
					limit: pageSize,
					total,
					onChange: (page, size) => setPage(page, size ?? pageSize),
				})}
				size="small"
				scroll={{ x: "max-content" }}
				dataSource={items}
				rowKey="id"
				columns={[
					{
						key: "#",
						title: "#",
						width: 60,
						align: "center",
						render: (_, __, index) => formatTableIndex(currentPage, pageSize, index),
					},
					{ key: "name", title: "Nomi", dataIndex: "name", ellipsis: true },
					{
						key: "type",
						title: "Turi",
						width: 120,
						render: (_, r: Plant) => TYPE_LABEL[r.type],
					},
					{
						key: "description",
						title: "Tavsifi",
						dataIndex: "description",
						ellipsis: true,
						render: (v: string | null) => v ?? "—",
					},
					{
						key: "actions",
						title: "Amallar",
						width: 80,
						align: "center",
						render: (_, record: Plant) => {
							const menuItems: MenuProps["items"] = [
								{
									key: "edit",
									icon: <EditOutlined />,
									label: "Tahrirlash",
									onClick: () => openEditModal(record),
								},
							];

							return (
								<Dropdown menu={{ items: menuItems }} trigger={["click"]}>
									<Button type="text" icon={<MoreOutlined />} size="small" />
								</Dropdown>
							);
						},
					},
				]}
			/>
			<PlantFormModal organizationId={organizationId} />
		</>
	);
}
